import type { OpcoesCotacao, TipoOleo } from '../../lib/cotacao';

export interface CamposOleo {
  tipoOleo: string;
  medidaOleo: string;
  modeloOleo: string;
  largura: string;
  altura: string;
  corte: string;
  acabamentos: string[];
}

type Opcoes = NonNullable<OpcoesCotacao['trocaOleo']>;

export function selecaoOleo(o: Opcoes, f: Pick<CamposOleo, 'tipoOleo' | 'medidaOleo'>) {
  const tipo = o.tipos.find((t) => t.nome === f.tipoOleo) ?? null;
  const medida = tipo?.medidas.find((m) => m.nome === f.medidaOleo) ?? null;
  return { tipo, medida };
}

export function padraoOleo(o: Opcoes, tipo?: TipoOleo): Partial<CamposOleo> {
  const t = tipo ?? o.tipos[0];
  const medida = t?.medidas[0];
  return {
    tipoOleo: t?.nome ?? '',
    medidaOleo: medida?.nome ?? '',
    modeloOleo: medida?.modelos.length === 1 ? medida.modelos[0]! : '',
    corte: medida?.corte ?? o.cortes[0] ?? '',
    acabamentos: [t?.acabamentoFixo ?? o.acabamentos[0] ?? ''].filter(Boolean),
  };
}

export function CamposTrocaOleo({
  o,
  f,
  mudar,
}: {
  o: Opcoes;
  f: CamposOleo;
  mudar: (p: Partial<CamposOleo>) => void;
}) {
  const { tipo, medida } = selecaoOleo(o, f);

  const trocarTipo = (nome: string) => {
    const t = o.tipos.find((x) => x.nome === nome);
    mudar(padraoOleo(o, t));
  };
  const trocarMedida = (nome: string) => {
    const m = tipo?.medidas.find((x) => x.nome === nome);
    mudar({
      medidaOleo: nome,
      modeloOleo: m?.modelos.length === 1 ? m.modelos[0]! : '',
      corte: m?.corte ?? o.cortes[0] ?? '',
    });
  };

  return (
    <>
      <div className="campo">
        <span>Tipo</span>
        <div className="segmentos" role="radiogroup" aria-label="Tipo">
          {o.tipos.map((t) => (
            <button
              key={t.nome}
              role="radio"
              aria-checked={f.tipoOleo === t.nome}
              className={f.tipoOleo === t.nome ? 'ativo' : ''}
              onClick={() => trocarTipo(t.nome)}
            >
              {t.nome}
            </button>
          ))}
        </div>
        {tipo?.descricao && <small className="oleo-descricao">{tipo.descricao}</small>}
      </div>

      {tipo && (
        <div className="campo">
          <span>Medida</span>
          <div className="chips" role="radiogroup" aria-label="Medida">
            {tipo.medidas.map((m) => (
              <button
                key={m.nome}
                role="radio"
                aria-checked={f.medidaOleo === m.nome}
                className={f.medidaOleo === m.nome ? 'ativo' : ''}
                onClick={() => trocarMedida(m.nome)}
              >
                {m.nome}
              </button>
            ))}
          </div>
          {medida && !medida.dimensoes && (
            <small className="oleo-descricao">Tamanho personalizado custa 10% a mais.</small>
          )}
        </div>
      )}

      {medida && !medida.dimensoes && (
        <div className="campo">
          <span>Medidas (cm)</span>
          <div className="medidas">
            <input
              id="cot-largura"
              className="input"
              inputMode="decimal"
              placeholder="Largura"
              value={f.largura}
              onChange={(e) => mudar({ largura: e.target.value })}
            />
            <span aria-hidden>×</span>
            <input
              id="cot-altura"
              className="input"
              inputMode="decimal"
              placeholder="Altura"
              value={f.altura}
              onChange={(e) => mudar({ altura: e.target.value })}
            />
          </div>
        </div>
      )}

      {medida && medida.modelos.length > 0 && (
        <label className="campo">
          <span>Modelo</span>
          {medida.modelos.length === 1 ? (
            <span className="oleo-fixo">{medida.modelos[0]}</span>
          ) : (
            <select
              className="input"
              value={f.modeloOleo}
              onChange={(e) => mudar({ modeloOleo: e.target.value })}
            >
              <option value="">Escolha o modelo</option>
              {medida.modelos.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          )}
        </label>
      )}

      <div className="cotacao-linha oleo-regras">
        {tipo?.impressao && (
          <div className="campo">
            <span>Impressão</span>
            <span className="oleo-fixo">{tipo.impressao}</span>
          </div>
        )}
        {medida &&
          (medida.corte ? (
            <div className="campo">
              <span>Corte</span>
              <span className="oleo-fixo">{medida.corte}</span>
            </div>
          ) : (
            <label className="campo">
              <span>Corte</span>
              <select
                className="input"
                value={f.corte}
                onChange={(e) => mudar({ corte: e.target.value })}
              >
                {o.cortes.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
          ))}
      </div>

      <div className="campo">
        <span>Acabamento</span>
        {tipo?.acabamentoFixo ? (
          <span className="oleo-fixo">{tipo.acabamentoFixo}</span>
        ) : (
          <div className="chips" role="radiogroup" aria-label="Acabamento">
            {o.acabamentos.map((a) => (
              <button
                key={a}
                role="radio"
                aria-checked={f.acabamentos[0] === a}
                className={f.acabamentos[0] === a ? 'ativo' : ''}
                onClick={() => mudar({ acabamentos: [a] })}
              >
                {a}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
